import { useEffect, useState } from 'react';
import { Bike, Copy, X } from 'lucide-react';
import type {
  DomicilioEnTableroVista,
  PedidoVista,
  SugerenciaEnvioVista,
} from '@shared/types';
import { NumberField } from '../ui/NumberField';
import { formatPrecio } from '@renderer/utils/formatters';
import { centavosAPesos, pesosACentavos } from '@shared/centavos';
import { mensajeCotizacionAlGrupo } from '@shared/mensajesWhatsapp';
// Sprint 77 — Cómo se le explica al operador de dónde salió la sugerencia.
import { explicarTarifa } from '@shared/zonaEnvio';
import { copiarAlPortapapeles } from '@renderer/lib/portapapeles';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';
import { zonaDeLaDireccion } from './CostoEnvioForm';

/**
 * Sprint 76 — Completar la cotización de un pedido que se dejó esperando.
 *
 * Es el mismo paso que `CostoEnvioForm`, pero para un pedido **ya guardado**: se abre desde el
 * tablero cuando el grupo de motomandados por fin contesta. Captura los dos datos que vuelven de
 * ahí —cuánto cobran y quién viene por él— y recalcula el total en el servidor.
 *
 * El servidor no deja tocar el envío si el pedido ya está pagado: mover el total contra un
 * comprobante que ya cuadró descuadra el corte del día sin que nadie entienda por qué.
 */
export function CotizarEnvioModal({
  pedido,
  nombreNegocio,
  monedaSimbolo,
  onCerrar,
  onGuardado,
}: {
  pedido: DomicilioEnTableroVista | null;
  nombreNegocio?: string | null;
  monedaSimbolo: string;
  onCerrar: () => void;
  onGuardado: (pedido: PedidoVista) => void;
}) {
  const [costoEnvio, setCostoEnvio] = useState(0);
  const [envioPagaCliente, setEnvioPagaCliente] = useState(true);
  /** Sprint 77 — El tercer destino del envío: lo absorbe el negocio. */
  const [envioCortesia, setEnvioCortesia] = useState(false);
  const [nombreRepartidor, setNombreRepartidor] = useState('');
  /**
   * Sprint 137 — ¿Se cobra al entregar?
   *
   * Aquí es donde se **confirma** el acuerdo para los dos pedidos que no pasan por la captura
   * completa: el que se dejó esperando la cotización, y el que llegó del enlace —donde el
   * cliente ya pudo pedirlo, pero nadie del negocio lo había autorizado todavía—. Sin esta
   * casilla, un pedido del enlace marcado por el cliente no tendría forma de salir a la
   * parrilla: `enviarACocina` exige que un usuario lo haya autorizado.
   */
  const [pagaAlEntregar, setPagaAlEntregar] = useState(false);
  const [pagaCon, setPagaCon] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiado, setCopiado] = useState(false);
  const [sugerencia, setSugerencia] = useState<SugerenciaEnvioVista | null>(null);

  useEffect(() => {
    if (!pedido) return;
    setCostoEnvio(pedido.costoEnvio ?? 0);
    setEnvioPagaCliente(pedido.envioPagaCliente ?? true);
    setEnvioCortesia(pedido.envioCortesia ?? false);
    setNombreRepartidor(pedido.nombreRepartidor ?? '');
    /*
      Sprint 137 — Se arranca con lo que el pedido ya dice. Si el cliente lo pidió desde el
      enlace, la casilla aparece marcada: quien atiende confirma en vez de volver a decidir.
    */
    setPagaAlEntregar(pedido.pagaAlEntregar === true);
    setPagaCon(pedido.pagaCon != null ? String(pedido.pagaCon / 100) : '');
    setError(null);
    setCopiado(false);
    setGuardando(false);
    setSugerencia(null);
    (async () => {
      const res = await window.api.pedidos.sugerenciaEnvio({
        latitud: pedido.clienteLatitud ?? undefined,
        longitud: pedido.clienteLongitud ?? undefined,
      });
      if (res.ok) setSugerencia(res.data);
    })();
  }, [pedido]);

  if (!pedido) return null;

  const copiarParaElGrupo = async () => {
    const texto = mensajeCotizacionAlGrupo({
      folio: pedido.numeroPedido,
      clienteLatitud: undefined,
      clienteLongitud: undefined,
      zona: zonaDeLaDireccion(pedido.clienteDireccion),
      minutosListo: 20,
      nombreNegocio,
    });
    const ok = await copiarAlPortapapeles(texto);
    setCopiado(ok);
    if (!ok) setError('No se pudo copiar el mensaje. Escríbelo a mano en el grupo.');
  };

  const guardar = async () => {
    setGuardando(true);
    setError(null);
    const res = await window.api.pedidos.actualizarEnvio({
      pedidoId: pedido.id,
      costoEnvio: Math.max(0, costoEnvio),
      envioPagaCliente,
      envioCortesia,
      nombreRepartidor: nombreRepartidor.trim() || undefined,
      // Sprint 137 — El acuerdo de cobrar al entregar, confirmado por quien está cotizando.
      pagaAlEntregar,
      pagaCon: (() => {
        if (!pagaAlEntregar) return undefined;
        const n = Number(pagaCon.replace(',', '.').trim());
        // Un texto que no es número se trata como "no lo dijo": el dato es de ayuda, no un requisito.
        return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : undefined;
      })(),
      // DT-080 — la versión que esta pantalla tenía al leer el pedido.
      version: pedido.version,
    });
    setGuardando(false);
    if (!res.ok) {
      setError(traducirErrorBackend(res.error, res.codigo));
      return;
    }
    onGuardado(res.data);
  };

  // El total que se le va a pedir al cliente: el envío contra entrega no pasa por la caja.
  const comida = pedido.total - (pedido.envioPagaCliente ? 0 : pedido.costoEnvio ?? 0);
  const totalACobrar =
    envioPagaCliente || envioCortesia ? comida : comida + Math.max(0, costoEnvio);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="card max-h-[90vh] w-full max-w-md overflow-auto">
        <div className="mb-3 flex items-start justify-between gap-2">
          <div>
            <h2 className="text-lg font-bold">¿Cuánto cuesta llevarlo?</h2>
            <p className="text-xs text-slate-500">
              Pedido #{String(pedido.numeroPedido).padStart(5, '0')} —{' '}
              {pedido.clienteNombre ?? 'sin nombre'}
            </p>
          </div>
          <button onClick={onCerrar} className="btn-ghost p-2" aria-label="Cerrar">
            <X className="h-4 w-4" />
          </button>
        </div>

        {pedido.clienteDireccion && (
          <p className="mb-3 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
            📍 {pedido.clienteDireccion}
          </p>
        )}

        <button
          type="button"
          onClick={copiarParaElGrupo}
          className="btn-ghost mb-3 min-h-[44px] w-full justify-center border border-slate-300 text-sm"
        >
          <Copy className="h-4 w-4" />
          {copiado ? '✓ Copiado para el grupo' : 'Copiar mensaje para el grupo'}
        </button>

        <div className="space-y-3">
          <div>
            <label className="label" htmlFor="cotCosto">
              Cuánto cobran
            </label>
            <NumberField
              id="cotCosto"
              min={0}
              decimals={2}
              className="input"
              value={centavosAPesos(costoEnvio)}
              onChange={(n) => setCostoEnvio(pesosACentavos(n))}
              placeholder="0"
            />
          </div>

          {/*
            Sprint 77 — Los atajos: lo que el negocio configuró por zona y lo que se ha cobrado
            antes a este mismo rumbo.

            Son **botones**, no un valor precargado. La regla del negocio es "siempre prefiero
            cotizar", y un default se acepta sin leer: un día sale un pedido con la tarifa del
            mes pasado y nadie se entera hasta el corte. Un atajo, en cambio, exige un toque —o
            sea, una decisión.
          */}
          {sugerencia && (sugerencia.zonas.length > 0 || sugerencia.aprendidas.length > 0) && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-blue-900">
                Lo que se ha cobrado por aquí
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {sugerencia.aprendidas.slice(0, 3).map((a) => (
                  <button
                    key={`hist-${a.tarifa}`}
                    type="button"
                    onClick={() => setCostoEnvio(a.tarifa)}
                    className="min-h-[38px] rounded-lg border border-blue-300 bg-white px-3 text-sm font-medium text-blue-900 hover:bg-blue-100"
                  >
                    {formatPrecio(a.tarifa, monedaSimbolo)}
                    <span className="ml-1 text-xs font-normal text-blue-700">
                      · {explicarTarifa(a)}
                    </span>
                  </button>
                ))}
                {sugerencia.zonas.map((z) => (
                  <button
                    key={`zona-${z.id}`}
                    type="button"
                    onClick={() => setCostoEnvio(z.tarifa)}
                    className="min-h-[38px] rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-100"
                  >
                    {z.nombre}: {formatPrecio(z.tarifa, monedaSimbolo)}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs text-blue-800">
                Es una referencia. Confírmala en el grupo antes de dársela al cliente.
              </p>
            </div>
          )}

          <div>
            <label className="label" htmlFor="cotRepartidor">
              ¿Quién viene por él?
            </label>
            <div className="flex items-center gap-2">
              <Bike className="h-4 w-4 shrink-0 text-slate-500" />
              <input
                id="cotRepartidor"
                className="input"
                value={nombreRepartidor}
                onChange={(e) => setNombreRepartidor(e.target.value)}
                placeholder="Juan, El güero, yo misma…"
                maxLength={80}
              />
            </div>
          </div>

          <fieldset className="space-y-2">
            <legend className="mb-1 text-xs font-medium text-slate-600">
              ¿Quién paga el envío?
            </legend>
            <label className="flex cursor-pointer items-start gap-2 rounded p-2 hover:bg-slate-50">
              <input
                type="radio"
                checked={envioPagaCliente}
                onChange={() => setEnvioPagaCliente(true)}
                className="mt-0.5"
              />
              <div className="text-sm">
                <div className="font-medium">Le paga a la moto al recibir</div>
                <div className="text-xs text-slate-500">
                  Le cobramos {formatPrecio(comida, monedaSimbolo)}. El envío no entra a la caja.
                </div>
              </div>
            </label>
            <label className="flex cursor-pointer items-start gap-2 rounded p-2 hover:bg-slate-50">
              <input
                type="radio"
                checked={!envioPagaCliente}
                onChange={() => setEnvioPagaCliente(false)}
                className="mt-0.5"
              />
              <div className="text-sm">
                <div className="font-medium">Lo paga junto, ahorita</div>
                <div className="text-xs text-slate-500">
                  Le cobramos {formatPrecio(comida + Math.max(0, costoEnvio), monedaSimbolo)}.
                  Nosotros le pagamos a la moto.
                </div>
              </div>
            </label>
          </fieldset>

          {/*
            Sprint 137 — Cuándo paga el pedido, en el mismo momento en que se fija el total.

            Este modal atiende los dos pedidos que no pasaron por la captura completa: el que se
            dejó esperando la cotización y el que llegó del enlace. En el segundo, la casilla
            puede venir ya marcada porque lo pidió el cliente — y lo que se hace aquí es
            **confirmarlo**, que es lo que le pone nombre al acuerdo.
          */}
          <fieldset className="space-y-2 border-t border-slate-200 pt-3">
            <legend className="mb-1 text-xs font-medium text-slate-600">
              ¿Cuándo paga el pedido?
            </legend>
            <label className="flex cursor-pointer items-start gap-2 rounded p-2 hover:bg-slate-50">
              <input
                type="radio"
                checked={!pagaAlEntregar}
                onChange={() => setPagaAlEntregar(false)}
                className="mt-0.5"
              />
              <div className="text-sm">
                <div className="font-medium">Transfiere antes de cocinarlo</div>
                <div className="text-xs text-slate-500">
                  Como siempre. No entra a la parrilla hasta ver el pago.
                </div>
              </div>
            </label>
            <label className="flex cursor-pointer items-start gap-2 rounded p-2 hover:bg-slate-50">
              <input
                type="radio"
                checked={pagaAlEntregar}
                onChange={() => setPagaAlEntregar(true)}
                className="mt-0.5"
              />
              <div className="text-sm">
                <div className="font-medium">Paga al recibir</div>
                <div className="text-xs text-slate-500">
                  Se cocina ahora y se cobra al entregarlo. Queda como dinero por cobrar, a tu
                  nombre.
                </div>
              </div>
            </label>
            {pagaAlEntregar && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
                <label className="label" htmlFor="cotPagaCon">
                  ¿Con cuánto va a pagar?{' '}
                  <span className="font-normal text-slate-600">(opcional)</span>
                </label>
                <input
                  id="cotPagaCon"
                  className="input"
                  inputMode="decimal"
                  value={pagaCon}
                  onChange={(e) => setPagaCon(e.target.value)}
                  placeholder="500"
                  maxLength={10}
                />
                <p className="mt-1 text-xs text-amber-900">
                  Para que la moto salga con el cambio. Si no lo dijo, déjalo vacío.
                </p>
              </div>
            )}
          </fieldset>

          {/*
            Sprint 77 — El tercer caso, el que faltaba: el envío corre por cuenta del negocio.

            Pasa de verdad (cliente frecuente, disculpa por un retraso) y hasta ahora no se podía
            ni registrar: se acababa capturando como envío $0, que es mentira — el negocio sí le
            pagó a la moto. Sin este dato, nadie puede sumar al final del mes cuánto costó ser
            amable, que es justo el número con el que se decide subir el mínimo de compra.
          */}
          <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4"
              checked={envioCortesia}
              onChange={(e) => setEnvioCortesia(e.target.checked)}
            />
            <span className="text-sm">
              <span className="font-medium">Este envío lo paga el negocio (cortesía)</span>
              <span className="mt-0.5 block text-xs text-slate-500">
                El cliente no lo paga. Se registra el costo para saber cuánto suma al mes.
              </span>
            </span>
          </label>

          <div className="rounded-lg bg-slate-50 px-3 py-2">
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-semibold">Le cobro ahora</span>
              <span className="font-mono text-xl font-bold text-primary">
                {formatPrecio(totalACobrar, monedaSimbolo)}
              </span>
            </div>
          </div>

          {error && (
            <div className="error-text" role="alert">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button onClick={onCerrar} className="btn-ghost" disabled={guardando}>
              Ahora no
            </button>
            <button onClick={guardar} className="btn-primary min-h-[44px]" disabled={guardando}>
              {guardando ? 'Guardando…' : 'Guardar y pasar a cobro'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
